import { Component, Input, OnInit, OnChanges, SimpleChanges, inject, Output, EventEmitter, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { SidebarItem } from './sidebar.model';
import { PermissionService } from '../../../../core/auth/permission.service';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-global-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatExpansionModule, TranslatePipe],
  templateUrl: './global-sidebar.html',
  styleUrls: ['./global-sidebar.scss']
})
export class GlobalSidebarComponent implements OnInit, OnChanges {
  @Input() menuItems: SidebarItem[] = [];
  @Output() itemClick = new EventEmitter<void>();

  private permissionService = inject(PermissionService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  filteredMenuItems: SidebarItem[] = [];
  expandedPanelId: string | null = null;
  nestedExpandedPanelId: string | null = null;

  /**
   * Set once filterMenuByPermissions has run — from ngOnChanges when an
   * @Input binding fires it (Angular always calls ngOnChanges before
   * ngOnInit), so ngOnInit's own call below is skipped rather than
   * deep-cloning and permission-filtering the same static menu tree twice.
   */
  private hasFilteredMenu = false;

  /**
   * The URL the menu highlights. Normally the router's URL, but a navigation can name another
   * menu route in its state (`state: { sidebarRoute: '/salary/service-book/update' }`) — for a
   * screen opened as a step of another menu's flow, so the sidebar stays on the menu the user
   * came from instead of jumping to the screen's own entry. Cleared by the next navigation.
   */
  private activeUrl = '';

  private resolveActiveUrl(url: string): string {
    const state = this.router.lastSuccessfulNavigation()?.extras?.state as { sidebarRoute?: unknown } | undefined;
    return typeof state?.sidebarRoute === 'string' && state.sidebarRoute ? state.sidebarRoute : url;
  }

  ngOnInit() {
    this.activeUrl = this.resolveActiveUrl(this.router.url);
    if (!this.hasFilteredMenu) {
      this.filteredMenuItems = this.filterMenuByPermissions(this.menuItems);
      this.updateExpandedPanelFromRoute(this.activeUrl);
    }

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => {
        this.activeUrl = this.resolveActiveUrl(e.urlAfterRedirects || e.url);
        this.updateExpandedPanelFromRoute(this.activeUrl);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['menuItems']) {
      this.hasFilteredMenu = true;
      this.filteredMenuItems = this.filterMenuByPermissions(this.menuItems);
      this.activeUrl = this.resolveActiveUrl(this.router.url);
      this.updateExpandedPanelFromRoute(this.activeUrl);
    }
  }

  /**
   * Recursively filters menu items based on user permissions
   */
  private filterMenuByPermissions(items: SidebarItem[]): SidebarItem[] {
    // Deep clone to avoid mutating the original input config
    const clonedItems = JSON.parse(JSON.stringify(items)) as SidebarItem[];

    return clonedItems.filter(item => {
      // If it has children, recursively filter them first
      if (item.children && item.children.length > 0) {
        item.children = this.filterMenuByPermissions(item.children);
        // Keep the parent container if it still contains visible children;
        // a route-less group whose children were all filtered out is hidden.
        if (item.children.length > 0) {
          return true;
        }
        if (!item.route) {
          return false;
        }
      }

      // If item is a leaf node or has its own required permission, check it
      if (item.permission && !this.permissionService.can(item.permission)) {
        return false;
      }

      return true;
    });
  }

  onItemClick() {
    this.itemClick.emit();
  }

  onPanelOpened(id: string) {
    this.expandedPanelId = id;
  }

  onPanelClosed(id: string) {
    if (this.expandedPanelId === id) {
      this.expandedPanelId = null;
    }
  }

  onNestedPanelOpened(id: string) {
    this.nestedExpandedPanelId = id;
  }

  onNestedPanelClosed(id: string) {
    if (this.nestedExpandedPanelId === id) {
      this.nestedExpandedPanelId = null;
    }
  }

  /**
   * Checks whether this top-level panel is currently the open accordion dropdown
   */
  isPanelExpanded(item: SidebarItem): boolean {
    return this.expandedPanelId === item.id;
  }

  private routeMatches(childRoute: string, url: string): boolean {
    const cleanUrl = url.split('?')[0].split('#')[0];
    const cleanChildRoute = childRoute.split('?')[0].split('#')[0];
    return cleanUrl === cleanChildRoute || cleanUrl.startsWith(cleanChildRoute + '/');
  }

  private itemMatchesUrl(item: SidebarItem, url: string): boolean {
    if (!item.children || item.children.length === 0) return false;
    return item.children.some(child => {
      if (child.route && this.routeMatches(child.route, url)) {
        return true;
      }
      if (child.children && child.children.length > 0) {
        return this.itemMatchesUrl(child, url);
      }
      return false;
    });
  }

  private updateExpandedPanelFromRoute(url: string) {
    for (const item of this.filteredMenuItems) {
      if (this.itemMatchesUrl(item, url)) {
        this.expandedPanelId = item.id;
        this.nestedExpandedPanelId = this.findNestedExpandedId(item, url);
        return;
      }
    }
    const isLeafActive = this.filteredMenuItems.some(
      item => (!item.children || item.children.length === 0) && item.route && this.routeMatches(item.route, url)
    );
    if (isLeafActive) {
      this.expandedPanelId = null;
      this.nestedExpandedPanelId = null;
    }
  }

  /**
   * Finds the 2nd-level child (of an already-matched top-level item) whose
   * own children (the 3rd-level links) contain the active route, so the
   * nested accordion auto-expands on route changes the same way the
   * top-level one already does — not just on a manual click of the nested
   * panel itself.
   */
  private findNestedExpandedId(item: SidebarItem, url: string): string | null {
    if (!item.children) return null;
    for (const child of item.children) {
      if (child.children && child.children.length > 0 && this.itemMatchesUrl(child, url)) {
        return child.id;
      }
    }
    return null;
  }

  /**
   * Returns true when the current URL starts with the child's base route,
   * AND no sibling route is a longer (more specific) match.
   *
   * Example: on /employees/accounts → "Account Details" wins over "All Employee"
   * because /employees/accounts is longer than /employees.
   */
  isChildActive(child: SidebarItem, siblings: SidebarItem[]): boolean {
    if (!child.route) return false;
    const url = this.activeUrl || this.router.url;

    if (!this.routeMatches(child.route, url)) return false;

    // Check if any sibling has a longer, more specific match
    const moreSpecificMatch = siblings.some(s =>
      s !== child &&
      s.route &&
      this.routeMatches(s.route, url) &&
      s.route.length > child.route!.length
    );

    return !moreSpecificMatch;
  }
}
